import { CloseFullscreenRounded, CloseSharp } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputBase,
} from '@mui/material';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import GrowDialogTransition from 'components/modal/GrowDialogTransition';
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { AttachmentType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import Thumbnails from 'components/common/dropzone/Thumbnails';
import { IMAGE_TYPES, DOCUMENT_TYPES } from 'components/utils/constants';
import Attachment2Icon from 'remixicon-react/Attachment2Icon';
import Upload from 'components/common/upload/Upload';

type PromptInputModalProps = {
  allowAttachments?: boolean;
  attachments?: AttachmentType[];
  open: boolean;
  enteredValue: string;
  onClose: () => void;
  onFullModalMinimized: (value: string) => void;
  onSend: (value: string) => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  handleFileUploads?: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRemoveAttachment?: (attachment: AttachmentType) => void;
};

function PromptInputModal({
  allowAttachments = false,
  attachments = [],
  open,
  enteredValue,
  onClose,
  onFullModalMinimized,
  onSend,
  setOpen,
  handleFileUploads,
  handleRemoveAttachment,
}: PromptInputModalProps) {
  const theme = useTheme();
  const [value, setValue] = useState(enteredValue);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(-1, -1);
        inputRef.current.focus();
      }
    }, 0);
  }, [inputRef, enteredValue]);

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      sx={{ '& .MuiPaper-root': { minHeight: '85%' } }}
      TransitionComponent={GrowDialogTransition}
    >
      <CustomDialogTitle id="dialog-title" sx={{ height: 70 }}>
        <Box display="flex" alignItems="center" justifyContent="flex-end">
          <IconButton
            onClick={() => {
              onFullModalMinimized(value);
              setOpen(false);
            }}
          >
            <CloseFullscreenRounded sx={(theme) => ({ color: theme.palette.custom.black })} />
          </IconButton>
          <IconButton
            onClick={() => {
              onClose();
              setOpen(false);
            }}
          >
            <CloseSharp sx={(theme) => ({ color: theme.palette.custom.black })} />
          </IconButton>
        </Box>
      </CustomDialogTitle>
      <DialogContent
        sx={{ py: 0, cursor: 'text' }}
        onClick={() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }}
      >
        <InputBase
          multiline
          fullWidth
          inputRef={inputRef}
          placeholder="Send a message"
          value={value}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setValue(event.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-start' }}>
        <Box width="100%">
          <Thumbnails
            showCoverPhotoIndicator={false}
            disableEdit
            smallView
            files={attachments}
            removeFile={handleRemoveAttachment}
          />
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              {allowAttachments && (
                <Upload
                  multiple
                  acceptedFiles={[...IMAGE_TYPES, ...DOCUMENT_TYPES]}
                  onChange={handleFileUploads}
                >
                  <IconButton sx={{ mr: 0.5 }}>
                    <Attachment2Icon fontSize="small" />
                  </IconButton>
                </Upload>
              )}
            </Box>
            <Button
              variant="contained"
              disabled={!value}
              onClick={() => {
                onSend(value);
                setOpen(false);
              }}
            >
              Send
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default PromptInputModal;
